import type { MicEngine } from './micPermission'

/**
 * QUEL NAVIGATEUR, DONC QUEL GUIDE DE SORTIE ? — module PUR (issue #190).
 *
 * Un site ne peut ni redemander le micro après un refus, ni lever le blocage à
 * la place de l'agent : il ne peut que lui montrer OÙ cliquer. Et cet endroit
 * dépend du navigateur. `detectMicBrowser` est une table User-Agent → guide,
 * sans effet de bord ; `readMicBrowserEnv` est le seul point qui lit
 * `navigator` / `window`, et il ne jette jamais.
 *
 * Le design system (§5sexies, DA-011) interdit un nom de navigateur CODÉ EN
 * DUR dans un bandeau : ici le nom est DÉTECTÉ, et le guide `generic` prend le
 * relais dès que la détection ne sait pas.
 */

export type MicGuideId =
  | 'chromium'
  | 'safari-mac'
  | 'ios'
  /** PWA installée sur l'écran d'accueil : pas de barre d'adresse, donc pas de « aA ». */
  | 'ios-pwa'
  | 'firefox'
  | 'generic'

export type MicBrowserBrand =
  | 'chrome'
  | 'edge'
  | 'brave'
  | 'arc'
  | 'opera'
  | 'safari'
  | 'firefox'
  | 'other'

export interface MicBrowser {
  engine: MicEngine
  guide: MicGuideId
  brand: MicBrowserBrand
  /** Nom affichable (« Chrome », « Edge »…) — `null` quand on ne sait pas. */
  name: string | null
  /**
   * Adresse des réglages micro, à COPIER : un lien `chrome://` n'est pas
   * cliquable depuis une page. `null` hors Chromium de bureau.
   */
  settingsUrl: string | null
}

export interface MicBrowserEnv {
  userAgent: string
  /** `navigator.userAgentData.brands` (Chromium seulement). */
  brands?: ReadonlyArray<{ brand: string }>
  maxTouchPoints?: number
  /** PWA en mode autonome (`navigator.standalone` ou `display-mode: standalone`). */
  standalone?: boolean
  /** `navigator.brave` existe (Brave masque sa marque dans l'UA). */
  brave?: boolean
  /** Arc expose une variable CSS propre ; son UA est celui de Chrome. */
  arc?: boolean
}

const CHROMIUM_BRANDS: Record<
  Exclude<MicBrowserBrand, 'safari' | 'firefox' | 'other'>,
  { name: string; scheme: string }
> = {
  chrome: { name: 'Chrome', scheme: 'chrome' },
  edge: { name: 'Edge', scheme: 'edge' },
  brave: { name: 'Brave', scheme: 'brave' },
  arc: { name: 'Arc', scheme: 'arc' },
  opera: { name: 'Opera', scheme: 'opera' },
}

export function detectMicBrowser(env: MicBrowserEnv): MicBrowser {
  const ua = env.userAgent ?? ''
  const brands = (env.brands ?? []).map((entry) => entry.brand)
  const hasBrand = (needle: string) => brands.some((brand) => brand.includes(needle))

  // 1) iOS / iPadOS — TOUS les navigateurs y sont WebKit (Chrome = CriOS…),
  //    donc tous suivent les réglages de Safari. Un iPad en « site pour
  //    ordinateur » se présente comme un Mac : seul l'écran tactile le trahit.
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (/Macintosh/.test(ua) && (env.maxTouchPoints ?? 0) > 1)
  if (isIos) {
    return {
      engine: 'webkit',
      guide: env.standalone ? 'ios-pwa' : 'ios',
      brand: 'safari',
      name: 'Safari',
      settingsUrl: null,
    }
  }

  // 2) Firefox (avant Chromium : son UA ne contient ni « Chrome » ni marque).
  if (/Firefox\//.test(ua)) {
    return {
      engine: 'gecko',
      guide: 'firefox',
      brand: 'firefox',
      name: 'Firefox',
      settingsUrl: null,
    }
  }

  // 3) Famille Chromium — la seule où un refus est DÉFINITIF.
  if (hasBrand('Chromium') || /Chrome\/|Chromium\/|Edg\//.test(ua)) {
    const brand: keyof typeof CHROMIUM_BRANDS = env.arc
      ? 'arc'
      : hasBrand('Microsoft Edge') || /Edg\//.test(ua)
        ? 'edge'
        : env.brave || hasBrand('Brave')
          ? 'brave'
          : hasBrand('Opera') || /OPR\//.test(ua)
            ? 'opera'
            : 'chrome'
    const { name, scheme } = CHROMIUM_BRANDS[brand]
    // Sur Android, les pages `chrome://settings/content/*` n'existent pas.
    const desktop = !/Android/.test(ua)
    return {
      engine: 'chromium',
      guide: 'chromium',
      brand,
      name,
      settingsUrl: desktop ? `${scheme}://settings/content/microphone` : null,
    }
  }

  // 4) Safari de bureau.
  if (/Safari\//.test(ua) && /Macintosh/.test(ua)) {
    return {
      engine: 'webkit',
      guide: 'safari-mac',
      brand: 'safari',
      name: 'Safari',
      settingsUrl: null,
    }
  }

  return { engine: 'other', guide: 'generic', brand: 'other', name: null, settingsUrl: null }
}

/** Lit l'environnement réel. Ne jette jamais ; un champ illisible reste absent. */
export function readMicBrowserEnv(): MicBrowserEnv {
  const env: MicBrowserEnv = { userAgent: '' }
  try {
    if (typeof navigator === 'undefined') return env
    const nav = navigator as Navigator & {
      userAgentData?: { brands?: ReadonlyArray<{ brand: string }> }
      standalone?: boolean
      brave?: unknown
    }
    env.userAgent = nav.userAgent ?? ''
    env.brands = nav.userAgentData?.brands
    env.maxTouchPoints = nav.maxTouchPoints
    env.brave = nav.brave !== undefined
    env.standalone = nav.standalone === true
    if (typeof window !== 'undefined') {
      if (!env.standalone && typeof window.matchMedia === 'function') {
        env.standalone = window.matchMedia('(display-mode: standalone)').matches
      }
      if (typeof document !== 'undefined' && typeof window.getComputedStyle === 'function') {
        env.arc =
          window
            .getComputedStyle(document.documentElement)
            .getPropertyValue('--arc-palette-title')
            .trim() !== ''
      }
    }
  } catch {
    // détection partielle : le guide générique prendra le relais
  }
  return env
}

export function currentMicBrowser(): MicBrowser {
  return detectMicBrowser(readMicBrowserEnv())
}
